// Unity ↔ three.js mapping, ported from the dashboard viewer
// (The-Alternatives-Web lib/dashboard/scene.ts) — proven against real content.
// Unity is left-handed Y-up; three is right-handed Y-up:
//   position: three = (x_offset, altitude, -z_offset)
//   yaw:      three.rotation.y = degToRad(-rotation)
//   scale:    uniform `size` (<=0 or null → 1)
import {CDN_BASE} from '../config'
import type {RawAnchorAsset} from '../api/types'

export function num(v: number | string | null | undefined): number | undefined {
  if (v === null || v === undefined || v === '') return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : undefined
}

export function truthy(v: boolean | number | string | null | undefined): boolean {
  return v === true || v === 1 || v === '1' || v === 'true'
}

export type AssetKind = 'model' | 'image' | 'video' | 'audio' | 'text' | 'unknown'

const EXT_KINDS: Record<string, AssetKind> = {
  glb: 'model',
  gltf: 'model',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  webp: 'image',
  mp4: 'video',
  mov: 'video',
  webm: 'video',
  wav: 'audio',
  mp3: 'audio',
  ogg: 'audio',
  aiff: 'audio',
}

/**
 * Dashboard-authoring metadata for "Scanned checkpoints": the row's `text`
 * holds a JSON scan reference (URL + orientation + geo), NOT display text,
 * and there is no file. Never render these — filter them out of any anchor's
 * asset list before spawning or counting.
 */
export function isEnvironmentScan(cfg: RawAnchorAsset): boolean {
  return (cfg.asset_type ?? '').toLowerCase().replace(/[\s_-]/g, '') === 'environmentscan'
}

// 3-tier kind resolution: asset_type → asset.media_type → path extension.
export function resolveAssetKind(cfg: RawAnchorAsset): AssetKind {
  const t = (cfg.asset_type ?? '').toLowerCase()
  if (t.includes('3d asset') || t.includes('object') || t.includes('model')) return 'model'
  if (t.includes('text')) return 'text'
  if (t.includes('image')) return 'image'
  if (t.includes('video')) return 'video'
  if (t.includes('sound') || t.includes('audio')) return 'audio'
  const m = (cfg.asset?.media_type ?? '').toLowerCase()
  if (m.includes('model')) return 'model'
  if (m.includes('image')) return 'image'
  if (m.includes('video')) return 'video'
  if (m.includes('audio') || m.includes('sound')) return 'audio'
  const path = cfg.asset?.path ?? ''
  const ext = path.split('.').pop()?.toLowerCase() ?? ''
  return EXT_KINDS[ext] ?? 'unknown'
}

export function assetUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined
  return `${CDN_BASE}/${path.replace(/^\//, '')}`
}

export interface AssetTransform {
  position: [number, number, number]
  yawRad: number
  scale: number
  spinRadPerSec: number
  billboard: boolean
}

// rotation_speed calibration: the wire value (e.g. 0.26 on real content) is far too
// slow to be visible as deg/sec; Unity applies it per-frame, so we scale by 60 to
// match a 60fps Unity feel. VERIFY against the Unity app on device (M0 checklist).
const SPIN_FRAME_RATE = 60

export function transformFromAsset(cfg: RawAnchorAsset): AssetTransform {
  const x = num(cfg.x_offset) ?? 0
  const y = num(cfg.altitude) ?? 0
  const z = num(cfg.z_offset) ?? 0
  const sizeRaw = num(cfg.size)
  const scale = sizeRaw !== undefined && sizeRaw > 0 ? sizeRaw : 1
  const yawDeg = num(cfg.rotation) ?? 0
  const spin = num(cfg.rotation_speed) ?? 0
  return {
    position: [x, y, -z],
    yawRad: (-yawDeg * Math.PI) / 180,
    scale,
    spinRadPerSec: (spin * SPIN_FRAME_RATE * Math.PI) / 180,
    billboard: truthy(cfg.always_face_user),
  }
}

export function isGeoLocated(lat: string | number | null | undefined, lng: string | number | null | undefined): boolean {
  const la = num(lat)
  const ln = num(lng)
  if (la === undefined || ln === undefined) return false
  return !(la === 0 && ln === 0)
}
